import { Box, MenuItem, Select, Skeleton, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { PlayerStatSection } from "../../../models/PlayerStats";
import { buildNav } from "./sectionNav";

/**
 * Section selector, rendered by each view rather than the layout so the
 * view-specific control bar can sit above it (see `PlayerStatsLayout`).
 *
 * One tab row plus up to two dropdowns, driven entirely by the served
 * `Section.nav` breadcrumbs — see `sectionNav.ts`.
 */
export function useSelectedSection(
  sections: PlayerStatSection[],
): [PlayerStatSection | null, (key: string) => void] {
  const [params, setParams] = useSearchParams();
  const requested = params.get("section");
  // A section the current garden doesn't serve — a link copied from the NBA page
  // to the prospect page, where impact sections don't exist — falls back rather
  // than rendering an empty grid. Pre-drill-down links (`?section=coverage`) land
  // here too, which is why the fallback matters more than it used to.
  const section =
    sections.find((s) => s.key === requested) ?? sections[0] ?? null;

  const setSection = (key: string) => {
    const updated = new URLSearchParams(params);
    updated.set("section", key);
    // Sort is section-specific; carrying it over would sort by a missing column.
    updated.delete("sort");
    updated.delete("dir");
    setParams(updated, { replace: true });
  };

  return [section, setSection];
}

/** Small enough to read as a breadcrumb affordance rather than a form control. */
function Caret(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 8 5" width="8" height="5" {...props}>
      <path d="M0 0h8L4 5z" fill="currentColor" />
    </svg>
  );
}

// The crumbs are deliberately sentence-case against the tabs' uppercase: rank
// reads from case, so the second level can sit at nearly the same optical
// weight without competing for the eye. Borderless for the same reason — two
// outlined selects read as a form, which is what made the old treatment feel
// like chrome bolted onto the tab strip rather than part of the page.
const CRUMB_SX = {
  fontSize: 12.5,
  fontWeight: 600,
  color: "#17191c",
  borderRadius: "2px",
  transition: "background-color 120ms ease-out",
  "&:hover": { backgroundColor: "#eef0f2" },
  "& .MuiSelect-select": {
    py: 0,
    pl: 0.75,
    pr: "19px !important",
    minHeight: "unset",
    // Fills all but 2px of the 26px row, so the hover chip is a real target
    // rather than a sliver floating in the middle of it.
    lineHeight: "22px",
  },
  "& .MuiSelect-icon": {
    right: 6,
    top: "calc(50% - 2px)",
    color: "#9aa1ab",
    transition: "color 120ms ease-out",
  },
  "&:hover .MuiSelect-icon": { color: "#17191c" },
};

function NavSelect({
  options,
  value,
  onChange,
}: {
  options: { label: string; sectionKey: string }[];
  value: string | null;
  onChange: (key: string) => void;
}) {
  return (
    <Select
      variant="standard"
      disableUnderline
      IconComponent={Caret}
      value={value ?? ""}
      onChange={(e) => {
        const picked = options.find((o) => o.label === e.target.value);
        if (picked) onChange(picked.sectionKey);
      }}
      sx={CRUMB_SX}
      MenuProps={{ slotProps: { paper: { sx: { borderRadius: 0 } } } }}
    >
      {options.map((o) => (
        <MenuItem key={o.label} value={o.label} sx={{ fontSize: 12.5 }}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
}

export default function SectionTabs({
  sections,
  section,
  onChange,
  loading,
}: {
  sections: PlayerStatSection[];
  section: PlayerStatSection | null;
  onChange: (key: string) => void;
  loading?: boolean;
}) {
  const nav = buildNav(sections, section);
  // One option is not a choice. Several actions carry a single breakdown today,
  // and a dropdown that can't change anything reads as broken.
  const levels = [
    { options: nav.subs, value: nav.activeSub },
    { options: nav.views, value: nav.activeView },
  ].filter((l) => l.options.length > 1);

  if (loading && !sections.length) {
    return (
      <Skeleton
        variant="rectangular"
        height={36}
        sx={{ border: "1px solid #dfe2e6" }}
      />
    );
  }

  return (
    <Box
      sx={{
        // Unselected tabs sit on the page's grey and the selected one lifts to
        // the white of the grid below it, so the active tab reads as physically
        // continuous with its table.
        backgroundColor: "#f9f9f9",
        border: "1px solid #dfe2e6",
        borderBottom: "none",
      }}
    >
      <Tabs
        value={nav.activeTab ?? false}
        onChange={(_e, label) => {
          const picked = nav.tabs.find((t) => t.label === label);
          if (picked) onChange(picked.sectionKey);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 36,
          // The red rule is drawn per-tab as an inset shadow rather than by the
          // sliding indicator — it has to sit flush inside the selected tab's
          // white panel, which a shared indicator can't do.
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTab-root": {
            // `py: 0` is what actually sets the height. MUI's default 12px
            // vertical padding plus the button line-height renders ~46px, so the
            // `minHeight` below never binds on its own.
            minHeight: 36,
            height: 36,
            py: 0,
            px: 2.2,
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "#6b7280",
            boxShadow: "inset 0 -1px 0 #e6e8eb",
          },
          "& .MuiTab-root.Mui-selected": {
            color: "#17191c",
            backgroundColor: "#ffffff",
            boxShadow: "inset 0 -3px 0 #cf0a2c",
          },
        }}
      >
        {nav.tabs.map((t) => (
          <Tab key={t.label} value={t.label} label={t.label} />
        ))}
      </Tabs>

      {/* The crumb row stays on the strip's grey and closes with a rule, so it
          groups upward with the tabs — it's navigation, not a grid control, and
          the toolbar directly below it is white. Flat sections render no row,
          and no rule: there the active tab's white running into the toolbar is
          the correct continuity. */}
      {levels.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1px",
            height: 26,
            px: 1.25,
            backgroundColor: "#f9f9f9",
            borderBottom: "1px solid #dfe2e6",
            overflowX: "auto",
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {levels.map((level, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "center", gap: "1px" }}
            >
              {i > 0 && (
                <Box
                  component="span"
                  sx={{
                    px: "2px",
                    fontSize: 15,
                    lineHeight: 1,
                    color: "#c3c8cf",
                    userSelect: "none",
                  }}
                >
                  ›
                </Box>
              )}
              <NavSelect
                options={level.options}
                value={level.value}
                onChange={onChange}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
